import { useState } from "react";
import { LuFolder, LuFolderOpen } from "react-icons/lu";
import {
  ProjectFilesListStatus,
  useProjectFilesListActions,
  useProjectFilesListFiles,
  useProjectFilesListStatus,
  useProjectFilesSelectedFilePath,
} from "../../../core_stores/useProjectFilesListStore";

interface FileNode {
  name: string;
  path: string;
  isDirectory: boolean;
  children?: FileNode[];
}

export function ProjectFilesTree() {
  const status = useProjectFilesListStatus();
  const files = useProjectFilesListFiles();
  const selectedFilePath = useProjectFilesSelectedFilePath();
  const { onFileSelected } = useProjectFilesListActions();

  function buildTree(): FileNode[] {
    const root: FileNode[] = [];
    const pathMap = new Map<string, FileNode>();

    const sortNodes = (nodes: FileNode[]) => {
      return nodes.sort((a, b) => {
        // If both are directories or both are files, sort by name
        if (a.isDirectory === b.isDirectory) {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        }
        // If one is a directory and one is a file, directory comes first
        return a.isDirectory ? -1 : 1;
      });
    };

    files.forEach((filePath) => {
      const processedPath = filePath.startsWith("/")
        ? filePath.slice(1)
        : filePath;
      const parts = processedPath.split("/");
      let currentPath = "";
      let parentNode = { children: root } as FileNode;

      parts.forEach((part, index) => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        const isLast = index === parts.length - 1;

        if (!pathMap.has(currentPath)) {
          const node: FileNode = {
            name: part,
            path: currentPath,
            isDirectory: !isLast,
            children: !isLast ? [] : undefined,
          };
          pathMap.set(currentPath, node);
          parentNode.children?.push(node);
          // Sort the children array after adding a new node
          if (parentNode.children) {
            sortNodes(parentNode.children);
          }
        }
        parentNode = pathMap.get(currentPath)!;
      });
    });

    return sortNodes(root);
  }

  if (status === ProjectFilesListStatus.LOADING_FILES_LIST) {
    return <LinearLoadingIndicator />;
  }

  return (
    <div className="flex-1 flex flex-col w-full overflow-hidden">
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <div className="min-w-fit p-2 pb-10">
          {buildTree().map((node) => (
            <FileTreeItem
              key={node.path}
              node={node}
              onSelect={onFileSelected}
              selectedFile={selectedFilePath ?? null}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface FileNode {
  name: string;
  path: string;
  isDirectory: boolean;
  children?: FileNode[];
}

function FileTreeItem({
  node,
  level = 0,
  onSelect,
  selectedFile,
}: {
  node: FileNode;
  level?: number;
  onSelect: (path: string) => void;
  selectedFile: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const onDirectoryClick = () => {
    if (node.isDirectory) {
      setIsOpen(!isOpen);
    } else {
      onSelect(node.path);
    }
  };

  return (
    <div>
      <div
        className={`${
          selectedFile === node.path
            ? "bg-primary-blue text-white"
            : "hover:bg-white/50 dark:hover:bg-black/25 text-light-text dark:text-dark-text"
        } flex items-center gap-2 cursor-pointer
          py-1 pr-4 rounded-md`}
        style={{ paddingLeft: `${level + 0.75}rem` }}
        onClick={onDirectoryClick}
      >
        {node.isDirectory ? isOpen ? <LuFolderOpen /> : <LuFolder /> : " "}
        <span className="text-sm">{node.name}</span>
      </div>
      {node.isDirectory &&
        isOpen &&
        node.children?.map((child) => (
          <FileTreeItem
            key={child.path}
            node={child}
            level={level + 1}
            onSelect={onSelect}
            selectedFile={selectedFile}
          />
        ))}
    </div>
  );
}

function LinearLoadingIndicator() {
  return (
    <>
      <style>
        {`
          .loading-bar-inner {
            animation: loading-animation 1s infinite ease-in-out;
          }
          
          @keyframes loading-animation {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(200%);
            }
          }
        `}
      </style>
      <div className="px-2">
        <div
          className="loading-bar relative rounded-full h-1 
        bg-white/75 dark:bg-black/20 overflow-hidden"
        >
          <div
            className="loading-bar-inner 
          absolute top-0 left-0
          h-full w-1/2
          bg-primary-blue rounded-full"
          />
        </div>
      </div>
    </>
  );
}
